/**
 * KinsCircle Phase 6B — AI Quality Feedback
 *
 * STRICTLY ADDITIVE. localStorage only. Honors STABILITY.md §12.4.
 *
 * Stores ONLY lightweight metadata:
 *   - timestamp
 *   - action ('story' | 'title' | …)
 *   - rating tag ('love' | 'needs_work' | 'too_generic' | …)
 *   - the AI usage entry id this feedback corresponds to (so the diagnostics
 *     card can correlate without ever reading the prompt or the output)
 *
 * NEVER stores:
 *   - the original user input (caption, names, location)
 *   - the AI's generated text
 *   - any sensitive content
 *
 * If the qualityFeedback feature flag is OFF, every helper is a no-op.
 */

import { isAIQualityFeedbackEnabled } from './featureFlag';
import type { AIAction } from './promptTemplates';

export type AIFeedbackTag =
  | 'love'
  | 'needs_work'
  | 'too_generic'
  | 'too_emotional'
  | 'wrong_tone'
  | 'inaccurate';

export const AI_FEEDBACK_TAGS: { value: AIFeedbackTag; label: string }[] = [
  { value: 'love', label: 'Love it' },
  { value: 'needs_work', label: 'Needs work' },
  { value: 'too_generic', label: 'Too generic' },
  { value: 'too_emotional', label: 'Too emotional' },
  { value: 'wrong_tone', label: 'Wrong tone' },
  { value: 'inaccurate', label: 'Inaccurate' },
];

export type AIFeedbackEntry = {
  id: string;
  ts: number;
  action: AIAction;
  tag: AIFeedbackTag;
  /** Correlates to AIUsageEntry.id (if available). Never references DB rows. */
  usageRef?: string;
};

const FB_LS_KEY = 'kinscircle.ai.feedback.log.v1';
const FB_MAX = 200;

const readFeedback = (): AIFeedbackEntry[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(FB_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, FB_MAX) : [];
  } catch {
    return [];
  }
};

const writeFeedback = (entries: AIFeedbackEntry[]): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(
      FB_LS_KEY,
      JSON.stringify(entries.slice(0, FB_MAX)),
    );
  } catch {
    /* non-fatal */
  }
};

/** Record a single piece of feedback. No-op if the flag is OFF. */
export const recordAIFeedback = (
  action: AIAction,
  tag: AIFeedbackTag,
  usageRef?: string,
): void => {
  if (!isAIQualityFeedbackEnabled()) return;
  const cur = readFeedback();
  const next: AIFeedbackEntry[] = [
    {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ts: Date.now(),
      action,
      tag,
      usageRef,
    },
    ...cur,
  ].slice(0, FB_MAX);
  writeFeedback(next);
};

export const readAIFeedbackLog = (): AIFeedbackEntry[] => readFeedback();
export const clearAIFeedbackLog = (): void => writeFeedback([]);

export type AIFeedbackSummary = {
  total: number;
  byTag: Record<AIFeedbackTag, number>;
  topTag: AIFeedbackTag | null;
  byAction: Record<string, number>;
};

export const summarizeAIFeedback = (): AIFeedbackSummary => {
  const log = readFeedback();
  const byTag: Record<AIFeedbackTag, number> = {
    love: 0,
    needs_work: 0,
    too_generic: 0,
    too_emotional: 0,
    wrong_tone: 0,
    inaccurate: 0,
  };
  const byAction: Record<string, number> = {};
  log.forEach((e) => {
    byTag[e.tag] = (byTag[e.tag] ?? 0) + 1;
    byAction[e.action] = (byAction[e.action] ?? 0) + 1;
  });
  let topTag: AIFeedbackTag | null = null;
  let max = 0;
  (Object.entries(byTag) as [AIFeedbackTag, number][]).forEach(([k, v]) => {
    if (v > max) {
      max = v;
      topTag = k;
    }
  });
  return { total: log.length, byTag, topTag, byAction };
};
